import { Component, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';

import { Observable, observable, Subject, Subscription } from 'rxjs';

import { LoginService } from '../../../../pages/login/login.service';
import { AppService, UserService } from '../../../../shared/service/user.service';

import { ConfirmService } from '../../../../shared/components/confirm/confirm.service';
import { timeout } from 'rxjs/operators';
import { AppSubject } from '../../../../shared/app-subject';
import { InvoiceService } from '../../../sold/customer/invoice/invoice.service';

interface RunValue {
  idValue?: string
  idValues?: any
  actionType: string
  taxCode?: string
  requireLoginKey?: boolean
  signingData?: any
}

@Injectable({
  providedIn: `root`
})
export class SigningService {

  readonly errorSubject: Subject<any>;
  readonly displaySubject: Subject<any>;
  addCTSSubscription?: Subscription;

  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    private confirmService: ConfirmService,
    private loginService: LoginService,
    private invoiceService: InvoiceService
  ) {
    this.http = new HttpClient(this.backend);
    this.errorSubject = new Subject<any>();
    this.displaySubject = new Subject<any>();
  }

  private runResolve?: (value: void | PromiseLike<void>) => void;
  private runReject?: (reason?: any) => void;

  run(runValue: RunValue): Promise<void> {
    AppSubject.setReloadData('');

    let signingType = UserService.get('SIGNING');
    const signingTypes = UserService.get('SIGNING_TYPES');

    if (signingTypes === 'NULL' && !signingType) {
      signingType = 'UsbTokenSigning';
    }

    if (signingTypes !== 'NULL' && !signingType && signingType !== 'newCert') {
      this.displaySubject.next('form1');
    }

    if (!this.runResolve) {
      return new Promise((resolve, reject) => {
        this.runResolve = (v) => {
          resolve(v);
          this.cleanupResolver();
        };
        this.runReject = (e) => {
          reject(e);
          this.cleanupResolver();
        };
        this.executeRun(runValue, signingType, signingTypes);
      });
    } else {
      this.executeRun(runValue, signingType, signingTypes);
      // Return a temporary promise that matches the lifecycle of the active one if needed,
      // but for simplicity, we can just return a promise that resolves when executeRun is done 
      // OR better, we just return the existing logic's intent but guarded.
      return Promise.resolve();
    }
  }

  private cleanupResolver(): void {
    this.runResolve = undefined;
    this.runReject = undefined;
  }

  cancel(): void {
    if (this.runReject) {
      this.runReject('Signing cancelled by user');
    }
    this.cleanupResolver();
    AppSubject.setReloadData('');
  }

  private executeRun(runValue: RunValue, signingType: string, signingTypes: string): void {
    UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
    if (!signingType && signingTypes !== 'NULL') {
      // Intended to show form1 if type selection is needed
    } else if (signingType === 'UsbTokenSigning' || signingType === 'newCert' || signingTypes === 'NULL') {
      if (runValue.idValues && runValue.idValues.length > 0) {
        this.handleUsbBatchSigning(runValue);
      } else {
        this.handleUsbSigning(runValue);
      }
    } else {
      this.handleRemoteSigningFlow(signingType);
    }
  }

  private handleUsbBatchSigning(runValue: RunValue): void {
    const isInvoiceBatch = runValue.actionType === 'invoiceBatchSigning';

    if (isInvoiceBatch) {
      this.invoiceService.putToWorkEffort(runValue.idValues).subscribe({
        next: ({ workEffortId }: any) => {
          this.handleUsbSigning({ idValue: workEffortId, actionType: 'invoiceBatchSigning' });
        },
        error: (err: any) => this.runReject?.(err)
      });
    } else {
      this.handleUsbSigning(runValue);
    }
  }

  private async handleUsbSigning(runValue: RunValue): Promise<void> {
    try {
      const testRes: any = await this.http.get('http://localhost:13579/test').pipe(timeout(1000)).toPromise();

      if (testRes?.receiveDataFromBrowser === "Y") {
        await this.runModernUsbFlow(runValue);
      } else {
        await this.runLegacyUsbFlow(runValue);
      }
    } catch (error: any) {
      if (error.status === 200) {
        await this.runLegacyUsbFlow(runValue);
      } else {
        await this.handleUsbError(error, runValue);
      }
    } finally {
      UserService.set(UserService.KEY.SIGNING, '');
    }
  }

  private async runModernUsbFlow(runValue: RunValue): Promise<void> {
    const commonUrl = `${window.location.origin}/api/rest/s1/iam-common`;

    try {
      let signingResult = runValue.signingData;

      if (!signingResult) {
        const data = {
          signingType: runValue.actionType,
          idValue: runValue.idValue,
          taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
          responseContentType: "application/json"
        };
        signingResult = await this.http.put(`${commonUrl}/signing`, data, this.getHttpOptions()).toPromise();
      }

      this.displaySubject.next('form3');
      const response: any = await this.http.post('http://localhost:13579/signing', signingResult).toPromise();

      if (response?.success) {
        const completeResult: any = await this.http.post(`${commonUrl}/complete-signing`, response, this.getHttpOptions()).toPromise();
        this.processFinalResult(completeResult);
      } else if (response?.errorMessage) {
        this.showErrorDialog(response);
        await this.completeSigning(runValue, "N");
        this.runReject?.(response);
      }
    } catch (error: any) {
      this.showErrorDialog(error?.error);
      this.handleFinalError();
      this.runReject?.(error);
      throw error;
    }
  }

  private async runLegacyUsbFlow(runValue: RunValue): Promise<void> {
    let loginKey = '';
    if (runValue.requireLoginKey !== false) {
      const apiKey: any = await this.loginService.getApiKey().toPromise();
      loginKey = apiKey.token;
    }

    const data = {
      baseUrl: window.location.origin + '/api',
      loginKey,
      csrfToken: AppService.get('MOQUI_SESSION_TOKEN'),
      actionType: runValue.actionType,
      idValue: runValue.idValue,
      taxCode: runValue.taxCode || UserService.getUser().partyTaxId
    };

    try {
      const response: any = await this.http.post('http://localhost:13579/signing', data).toPromise();
      if (response?.success) {
        AppSubject.setReloadData('success');
        this.runResolve?.();
      } else if (response?.errorMessage) {
        this.showErrorDialog(response);
        AppSubject.setReloadData('error');
        this.runReject?.(response);
      }
    } catch (error) {
      this.errorSubject.next(error);
      this.runReject?.(error);
      throw error;
    }
  }

  private async handleUsbError(error: any, runValue: RunValue): Promise<void> {
    await this.completeSigning(runValue, "N");
    this.errorSubject.next(error);
    this.runReject?.(error);
  }

  private async completeSigning(runValue: RunValue, success: string): Promise<void> {
    const url = `${window.location.origin}/api/rest/s1/iam-common/complete-signing`;
    const input = {
      success,
      idValue: runValue.idValue,
      signingType: runValue.actionType
    };
    try {
      const result: any = await this.http.post(url, input, this.getHttpOptions()).toPromise();
      if (result?.idValue) {
        AppSubject.setReloadData(success === "Y" ? 'success' : 'error');
        this.displaySubject.next('closeForm3');
        if (success === "Y") this.runResolve?.();
        else this.runReject?.(result);
      }
    } catch (error) {
      this.runReject?.(error);
    }
  }

  private async handleRemoteSigningFlow(signingType: string): Promise<void> {
    const url = `${window.location.origin}/api/rest/s1/iam-common/remote-signing?partyClassificationId=${signingType}`;
    try {
      const result: any = await this.http.get(url, this.getHttpOptions()).toPromise();
      if (result.listData) {
        UserService.set(UserService.KEY.REMOTE_SIGNING, JSON.stringify(result.listData));
        this.displaySubject.next('form2');
      } else {
        this.showErrorDialog({ errorMessage: 'Không tìm thấy thông tin chứng thư số từ xa' });
        UserService.set(UserService.KEY.SIGNING, '');
        this.runReject?.('No remote signing data found');
      }
    } catch (error: any) {
      this.showErrorDialog(error?.error);
      UserService.set(UserService.KEY.SIGNING, '');
      this.runReject?.(error);
    }
  }

  private processFinalResult(result: any): void {
    if (result?.idValue) {
      AppSubject.setReloadData('success');
      this.displaySubject.next('closeForm3');
      this.runResolve?.();
    } else if (result?.errorMessage) {
      this.showErrorDialog(result);
      this.handleFinalError();
      this.runReject?.(result);
    }
  }

  private handleFinalError(): void {
    AppSubject.setReloadData('error');
    this.displaySubject.next('closeForm3');
  }

  private showErrorDialog(error: any): void {
    if (!error) return;
    this.confirmService.setConfirmDialog({
      message: `[${error.errorCode || 'Error'}] ${error.errorMessage || error.errors || 'Unknown error'}`,
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });
  }

  private getHttpOptions() {
    return {
      headers: { "x-csrf-token": AppService.get('MOQUI_SESSION_TOKEN') }
    };
  }

  remoteSigning(data: any): Observable<any> {
    const obs = this.http.post(`/api/rest/s1/iam-common/remote-signing`, data, this.getHttpOptions());
    return new Observable(subscriber => {
      obs.subscribe({
        next: (val) => {
          UserService.set(UserService.KEY.SIGNING, '');
          this.runResolve?.();
          subscriber.next(val);
        },
        error: (err) => {
          this.showErrorDialog(err?.error);
          UserService.set(UserService.KEY.SIGNING, '');
          this.runReject?.(err);
          subscriber.error(err);
        },
        complete: () => subscriber.complete()
      });
    });
  }
}
